import React from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigationStore } from '@/stores/navigation'
import CustomerLogos from '@/components/common/CustomerLogos'
import { 
  CalendarDays, 
  Users2, 
  Workflow, 
  Link as LinkIcon, 
  Gauge, 
  Landmark, 
  ShieldCheck, 
  FileCheck, 
  Lock, 
  BarChart3, 
  Stethoscope, 
  Building2, 
  CalendarCheck, 
  TrendingUp, 
  Clock,
  ClipboardCheck 
} from "lucide-react"

const CaseCX: React.FC = () => {
  const { t } = useTranslation()
  const navigationStore = useNavigationStore()

  const handleBookDemo = () => {
    navigationStore.openDemoModal()
  }
  
  const features = [
    { 
      title: t('casecx.features.items.flexibleWorkflow.title'), 
      body: t('casecx.features.items.flexibleWorkflow.body'), 
      Icon: Workflow 
    },
    { 
      title: t('casecx.features.items.customForms.title'), 
      body: t('casecx.features.items.customForms.body'), 
      Icon: FileCheck 
    },
    { 
      title: t('casecx.features.items.integratedNotifications.title'), 
      body: t('casecx.features.items.integratedNotifications.body'), 
      Icon: CalendarDays 
    },
    { 
      title: t('casecx.features.items.slaTracking.title'), 
      body: t('casecx.features.items.slaTracking.body'), 
      Icon: Gauge 
    },
    { 
      title: t('casecx.features.items.closedLoopReferrals.title'), 
      body: t('casecx.features.items.closedLoopReferrals.body'), 
      Icon: Landmark 
    },
    { 
      title: t('casecx.features.items.schedulingBuiltIn.title'), 
      body: t('casecx.features.items.schedulingBuiltIn.body'), 
      Icon: CalendarCheck 
    },
    { 
      title: t('casecx.features.items.careCoordination.title'), 
      body: t('casecx.features.items.careCoordination.body'), 
      Icon: Users2 
    },
    { 
      title: t('casecx.features.items.programsCarePlans.title'), 
      body: t('casecx.features.items.programsCarePlans.body'), 
      Icon: Stethoscope 
    },
    { 
      title: t('casecx.features.items.taskAssignment.title'), 
      body: t('casecx.features.items.taskAssignment.body'), 
      Icon: ClipboardCheck 
    },
  ]

  const useCases = [
    { 
      title: t('casecx.useCases.items.caseIntake.title'), 
      body: t('casecx.useCases.items.caseIntake.body'), 
      Icon: FileCheck 
    },
    { 
      title: t('casecx.useCases.items.multiStepServices.title'), 
      body: t('casecx.useCases.items.multiStepServices.body'), 
      Icon: Stethoscope 
    },
    { 
      title: t('casecx.useCases.items.publicHealthCoordination.title'), 
      body: t('casecx.useCases.items.publicHealthCoordination.body'), 
      Icon: Building2 
    },
    { 
      title: t('casecx.useCases.items.reentryProbation.title'), 
      body: t('casecx.useCases.items.reentryProbation.body'), 
      Icon: Landmark 
    },
  ]

  const whyBullets = [
    {
      title: t('casecx.whyCasecx.items.outcomeDriven.title'),
      details: t('casecx.whyCasecx.items.outcomeDriven.details', { returnObjects: true }) as string[],
      Icon: Workflow,
    },
    {
      title: t('casecx.whyCasecx.items.operateStandalone.title'),
      details: t('casecx.whyCasecx.items.operateStandalone.details', { returnObjects: true }) as string[],
      Icon: LinkIcon,
    },
    {
      title: t('casecx.whyCasecx.items.engageClients.title'),
      details: t('casecx.whyCasecx.items.engageClients.details', { returnObjects: true }) as string[],
      Icon: Users2,
    },
    {
      title: t('casecx.whyCasecx.items.provenAcrossSectors.title'),
      details: t('casecx.whyCasecx.items.provenAcrossSectors.details', { returnObjects: true }) as string[],
      Icon: Landmark,
    },
    {
      title: t('casecx.whyCasecx.items.actionableInsights.title'),
      details: t('casecx.whyCasecx.items.actionableInsights.details', { returnObjects: true }) as string[],
      Icon: BarChart3,
    },
    {
      title: t('casecx.whyCasecx.items.rapidTimeToValue.title'),
      details: t('casecx.whyCasecx.items.rapidTimeToValue.details', { returnObjects: true }) as string[],
      Icon: FileCheck,
    },
  ]

  const faqs = [
    { 
      q: t('casecx.faq.items.integration.question'), 
      a: t('casecx.faq.items.integration.answer'), 
      Icon: LinkIcon 
    },
    { 
      q: t('casecx.faq.items.telehealth.question'), 
      a: t('casecx.faq.items.telehealth.answer'), 
      Icon: CalendarDays 
    },
    { 
      q: t('casecx.faq.items.security.question'), 
      a: t('casecx.faq.items.security.answer'), 
      Icon: ShieldCheck 
    },
    { 
      q: t('casecx.faq.items.implementation.question'), 
      a: t('casecx.faq.items.implementation.answer'), 
      Icon: FileCheck 
    },
    { 
      q: t('casecx.faq.items.pricing.question'), 
      a: t('casecx.faq.items.pricing.answer'), 
      Icon: BarChart3 
    },
  ]

  return (
    <div className="min-h-screen w-full text-white">
      <main className="bg-white text-slate-800">
        {/* HERO */}
        <header className="w-full" style={{ background: "linear-gradient(90deg, #395091 0%, #559EBA 100%)" }}>
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20 text-white">
            <div className="mb-8 sm:mb-10">
              <h2 className="text-6xl sm:text-7xl md:text-8xl leading-none tracking-tight">
                <span >case</span>
                <span className="font-bold text-[#7AC943]">CX</span>
                <sup className="align-super text-2xl sm:text-3xl md:text-4xl ml-1">™</sup>
              </h2>
            </div>
            <div className="max-w-3xl">
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3 sm:mb-4">{t('casecx.hero.title')}</h1>
              <p className="text-base sm:text-lg md:text-xl/8 opacity-95">{t('casecx.hero.subtitle')}</p>
              <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                <button 
                  onClick={handleBookDemo}
                  className="w-full sm:w-auto text-center bg-white text-[#395091] font-semibold px-6 py-3 rounded-lg shadow-md hover:bg-gray-100 transition"
                >
                  {t('common.bookDemo')}
                </button>
                <a
                  href="#case-study"
                  className="w-full sm:w-auto text-center bg-white/10 backdrop-blur-sm border border-white/20 text-white font-semibold px-6 py-3 rounded-lg hover:bg-white/20 transition"
                >
                  {t("common.downloadCaseStudy")}
                </a>
              </div>
            </div>
          </div>
        </header>

        {/* FEATURES (alt bg) */}
        <section id="features" className="relative">
          <div className="absolute inset-0 -z-10 bg-gradient-to-b from-white via-[#F7FAFF] to-white"/>
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-14">
            <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">{t('casecx.features.title')}</h3>
            <p className="text-slate-600 mb-6">{t('casecx.features.subtitle')}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
              {features.map(({ title, body, Icon }, i) => (
                <div key={i} className="rounded-2xl border border-slate-200 p-5 sm:p-6 shadow-sm hover:shadow-md transition bg-white/80 backdrop-blur-[1px]">
                  <div className="flex items-start gap-3 mb-2">
                    <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-[#F0F5FF]">
                      <Icon className="w-5 h-5 text-[#395091]" aria-hidden="true" />
                    </span>
                    <h4 className="font-semibold text-slate-900 text-lg leading-snug">{title}</h4>
                  </div>
                  <p className="text-slate-600 text-base leading-relaxed">{body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* USE CASES (card gradient bg) */}
        <section id="use-cases" className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-14">
          <div className="rounded-3xl p-6 sm:p-8" style={{background:"linear-gradient(135deg,#EDF7FF 0%,#F9FBFF 60%,#FFFFFF 100%)"}}>
            <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6 sm:mb-8">{t('casecx.useCases.title')}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
              {useCases.map(({ title, body, Icon }, i) => (
                <div key={i} className="rounded-2xl bg-gradient-to-tr from-[#F0F5FF] to-white border border-slate-200 p-5 sm:p-6 shadow-sm">
                  <div className="flex items-start gap-3 mb-2">
                    <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-[#E3ECFF]">
                      <Icon className="w-5 h-5 text-[#395091]" aria-hidden="true" />
                    </span>
                    <h4 className="font-semibold text-slate-900 text-lg leading-snug">{title}</h4>
                  </div>
                  <p className="text-slate-600 text-base leading-relaxed">{body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CASE STUDY + WHY (soft split bg) */}
        <section
          id="case-study"
          className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-14 scroll-mt-24 md:scroll-mt-28"
        >
          <div className="absolute inset-0 -z-10 bg-gradient-to-tr from-white via-[#F8FAFF] to-white"/>
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-14">
            <div className="grid lg:grid-cols-2 gap-8 sm:gap-10 items-start">
              <div>
                <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3 sm:mb-4">{t('casecx.caseStudy.title')}</h3>
                <p className="text-slate-600 leading-relaxed mb-4">{t('casecx.caseStudy.description')}</p>
                <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
                <a
                  href="/use-case-finder?Org=LDOC"
                  className="inline-block rounded-lg bg-[#395091] text-white px-5 py-3 font-semibold hover:opacity-90 text-center"
                >
                  {t("common.viewCaseStudy")}
                </a>
                <a
                  href="/use-case-finder"
                  className="inline-block text-[#395091] font-semibold hover:underline text-center"
                >
                  {t("casecx.caseStudy.seeMoreStories")}
                </a>
                  
                </div>
                <CustomerLogos logoUids={["rady", "dfci", "nhs"]} />
        </div>
        
              <div className="rounded-2xl border border-slate-200 p-5 sm:p-6 shadow-sm bg-gradient-to-br from-[#EDF2FF] via-white to-[#F9FAFB]">
                <h4 className="font-bold text-slate-900 mb-3 text-3xl md:text-4xl">{t('casecx.whyCasecx.title')}</h4>
                <p className="text-slate-700 text-base leading-relaxed mb-4">{t('casecx.whyCasecx.description')}</p>
                <ul className="space-y-3 text-base">
                  {whyBullets.map(({ title, details, Icon }, idx) => (
                    <li key={idx}>
                      <details className="group">
                        <summary className="cursor-pointer font-medium text-slate-900 flex items-center gap-2">
                          <Icon className="w-5 h-5 text-[#395091]" /> {title}
                        </summary>
                        <div className="mt-2 text-slate-600">
                          <ul className="list-disc ml-5 space-y-1">
                            {details.map((d, i) => (
                              <li key={i}>{d}</li>
                            ))}
                          </ul>
                        </div>
                      </details>
                    </li>
                  ))}
                </ul>
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
                  <div className="p-4 rounded-xl bg-white shadow-sm border border-slate-200">
                    <TrendingUp className="w-6 h-6 mx-auto text-[#395091] mb-2" />
                    <p className="text-2xl font-bold text-slate-900">{t('casecx.whyCasecx.stats.fasterIntake')}</p>
                    <p className="text-slate-600 text-sm">{t('casecx.whyCasecx.stats.fasterIntakeLabel')}</p>
                  </div>
                  <div className="p-4 rounded-xl bg-white shadow-sm border border-slate-200">
                    <Clock className="w-6 h-6 mx-auto text-[#395091] mb-2" />
                    <p className="text-2xl font-bold text-slate-900">{t('casecx.whyCasecx.stats.fewerMissedDeadlines')}</p>
                    <p className="text-slate-600 text-sm">{t('casecx.whyCasecx.stats.fewerMissedDeadlinesLabel')}</p>
                  </div>
                  <div className="p-4 rounded-xl bg-white shadow-sm border border-slate-200">
                    <Users2 className="w-6 h-6 mx-auto text-[#395091] mb-2" />
                    <p className="text-2xl font-bold text-slate-900">{t('casecx.whyCasecx.stats.improvedClientEngagement')}</p>
                    <p className="text-slate-600 text-sm">{t('casecx.whyCasecx.stats.improvedClientEngagementLabel')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* TRUST & COMPLIANCE (distinct section bg) */}
        <section className="relative border-t border-slate-200">
          <div className="absolute inset-0 -z-10 bg-[#F6F8FC]"/>
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-14">
            <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6">{t('casecx.trust.title')}</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
              <div className="flex flex-col items-center gap-2">
                <ShieldCheck className="w-8 h-8 text-[#395091]" />
                <span className="text-slate-700 font-medium">{t('casecx.trust.hipaa')}</span>
              </div>
              <div className="flex flex-col items-center gap-2">
                <FileCheck className="w-8 h-8 text-[#395091]" />
                <span className="text-slate-700 font-medium">{t('casecx.trust.soc2')}</span>
              </div>
              <div className="flex flex-col items-center gap-2">
                <Lock className="w-8 h-8 text-[#395091]" />
                <span className="text-slate-700 font-medium">{t('casecx.trust.sso')}</span>
              </div>
              <div className="flex flex-col items-center gap-2">
                <ShieldCheck className="w-8 h-8 text-[#395091]" />
                <span className="text-slate-700 font-medium">{t('casecx.trust.gdpr')}</span>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ (alt bg) */}
        <section className="relative border-t border-slate-200">
          <div className="absolute inset-0 -z-10 bg-gradient-to-b from-white to-[#F7FAFF]"/>
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-14">
            <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6">{t('casecx.faq.title')}</h3>
            <ul className="space-y-4">
              {faqs.map(({ q, a, Icon }, i) => (
                <li key={i} className="border border-slate-200 rounded-lg p-4 bg-white/90 backdrop-blur">
                  <details className="group">
                    <summary className="flex items-center gap-2 font-medium cursor-pointer text-slate-900">
                      <Icon className="w-5 h-5 text-[#395091]" /> {q}
                    </summary>
                    <p className="mt-2 text-slate-600 leading-relaxed">{a}</p>
                  </details>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* FINAL CTA (brand gradient) */}
        <section id="contact" className="relative mt-6 sm:mt-8">
          <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, #395091 0%, #559EBA 100%)" }} />
          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-14 text-white">
            <div className="grid lg:grid-cols-2 gap-8 sm:gap-10 items-center">
              <div>
                <h3 className="text-2xl sm:text-3xl font-bold mb-2 sm:mb-3">{t('casecx.cta.title')}</h3>
                <p className="opacity-90 text-base sm:text-lg">{t('casecx.cta.subtitle')}</p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 lg:justify-end w-full sm:w-auto">
                <button 
                  onClick={handleBookDemo}
                  className="w-full sm:w-auto text-center bg-white text-[#395091] font-semibold px-6 py-3 rounded-lg shadow-md hover:bg-gray-100 transition"
                >
                  {t('common.bookDemo')}
                </button>
              </div>
        </div>
      </div>
        </section>

        {/* FOOTER NOTE (muted bg) */}
        <section className="relative">
          <div className="absolute inset-0 -z-10 bg-[#F6F8FC]"/>
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-10 text-slate-500 text-sm">
            {t('casecx.footer')}
          </div>
        </section>
      </main>
    </div>
  )
}

export default CaseCX


